<template>
  <div class="order-details">
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading && order"
      class="order-details-content"
    >
      <div class="order-details-header">
        <div class="title-text">
          Your Order
        </div>
      </div>
      <div class="left-column">
        <div class="card">
          <div class="header">
            General Information
          </div>
          <div class="content">
            <TextInput
              v-model="order.buildTypeName"
              label="Build Type"
              class="input"
              :disabled="true"
            />
            <TextInput
              v-if="order.cartridgeName != ''"
              v-model="order.cartridgeName"
              label="Cartridge"
              class="input"
              :disabled="true"
            />
            <label class="input-label">Scheduled Date</label>
            <div class="scheduled-date">
              {{ order.scheduledDate ? $methods.formatDate(order.scheduledDate) : 'Pending' }}
            </div>
            <NumericInput
              v-if="order.partsDiscountPercentage > 0"
              v-model="order.partsDiscountPercentage"
              label="Parts Discount %"
              class="input"
              :disabled="true"
            />
            <NumericInput
              v-if="order.servicesDiscountPercentage > 0"
              v-model="order.servicesDiscountPercentage"
              label="Services Discount %"
              class="input"
              :disabled="true"
            />
            <label class="input-label">
              Total
            </label>
            <div class="total">
              {{ $methods.formatPrice(order.total) }} 
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Your Information
          </div>
          <div class="content">
            <div class="customer-tile">
              <div class="customer-tile-content">
                <div
                  v-if="order.customer.firstName || order.customer.lastName"
                  class="name"
                >
                  {{ order.customer.firstName }} {{ order.customer.lastName }}
                </div>
                <div
                  v-if="
                    order.customer.addressLine1 ||
                      order.customer.addressLine2 ||
                      order.customer.city ||
                      stateDisplayValue ||
                      order.customer.postalCode
                  "
                  class="address"
                >
                  <div v-if="order.customer.addressLine1">
                    {{ order.customer.addressLine1 }}
                  </div>
                  <div v-if="order.customer.addressLine2">
                    {{ order.customer.addressLine2 }}
                  </div>
                  <div
                    v-if="
                      order.customer.city ||
                        stateDisplayValue ||
                        order.customer.postalCode
                    "
                  >
                    <span
                      v-if="order.customer.city"
                    >{{ order.customer.city }}, </span><span v-if="stateDisplayValue">{{
                      stateDisplayValue + " "
                    }}</span><span v-if="order.customer.postalCode">{{
                      order.customer.postalCode
                    }}</span>
                  </div>
                </div>
                <a v-if="order.customer.phoneNumber">
                  {{ $methods.formatPhoneNumber(order.customer.phoneNumber) }}
                </a>
                <a v-if="order.customer.email">
                  {{ order.customer.email }}
                </a>
              </div>
              <DotsSvg
                :tabindex="0"
                @keyup.enter="showMenu = true"
                @click.stop="showMenu = true"
              />
              <div
                v-if="showMenu"
                class="menu"
              >
                <div v-click-outside="closeMenu">
                  <div
                    class="menu-option"
                    :tabindex="0"
                    @keyup.enter="onEditAccountInformationClicked"
                    @click="onEditAccountInformationClicked"
                    @blur="showMenu = false"
                  >
                    Edit Your Information
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Your Payments
          </div>
          <div class="content">
            <div
              v-for="payment in order.payments"
              :key="payment.amount"
              class="payment"
            >
              <div class="price">
                {{ $methods.formatPrice(payment.amount) }}
              </div>
              <div class="received-by">
                Received {{ $methods.formatDate(payment.receivedDate) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="right-column">
        <div class="card">
          <div class="header">
            Build Steps
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort">
                      <div>
                        PROCESS NAME
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        PROCESS STATUS
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="buildStep in order.buildSteps"
                    :key="buildStep.processName"
                  >
                    <td>
                      {{ buildStep.processName }}
                    </td>
                    <td>
                      {{ statusText(buildStep.processStatus) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Your Supplied Parts
          </div>
          <div class="content customer-supplied-parts">
            {{ order.customerSuppliedParts }}
          </div>
        </div>
        <div class="card">
          <div class="header">
            Parts
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort">
                      <div>
                        Item
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Status
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody v-if="order.parts.length > 0">
                  <tr
                    v-for="orderPart in order.parts"
                    :key="orderPart.name"
                  >
                    <td>{{ orderPart.name }}</td>
                    <td>{{ $methods.formatPrice(orderPart.price) }}</td>
                    <td>{{ orderPartStatusToDisplayValue(orderPart.status) }}</td>
                    <td class="notes">
                      <CustomerNoteTile
                        v-for="note in orderPart.notes"
                        :key="note.content"
                        :note="note"
                      />
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="4"
                      class="message"
                    >
                      There are no parts on your order.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Services
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort">
                      <div>
                        Name
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Description
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Fields
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Completed?
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody v-if="order.services.length > 0">
                  <tr
                    v-for="orderService in order.services"
                    :key="orderService.name"
                  >
                    <td>{{ orderService.name }}</td>
                    <td class="description">
                      {{ orderService.description }}
                    </td>
                    <td>{{ $methods.formatPrice(orderService.price) }}</td>
                    <td class="services">
                      <div
                        v-for="(serviceFieldValue, index) in orderService.serviceFieldValues"
                        :key="index"
                        class="service"
                      >
                        <TextInput
                          v-model="serviceFieldValue.value"
                          :label="serviceFieldValue.name"
                          class="input"
                          :disabled="true"
                        />
                      </div>
                    </td>
                    <td class="notes">
                      <CustomerNoteTile
                        v-for="note in orderService.notes"
                        :key="note.content"
                        :note="note"
                      />
                    </td>
                    <td>{{ orderService.completed ? 'Yes' : 'No' }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="6"
                      class="message"
                    >
                      There are no services on your order.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import CustomerNoteTile from '@/components/elements/CustomerNoteTile.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { CustomerOrderService } from '@/services/customerOrder.service'
import { CustomerOrderDetails } from '@/@types/responses/customer_order/CustomerOrderDetails'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'MyOrder',
  components: {
    CustomerNoteTile,
    DotsSvg,
    LoadingPulse,
    NumericInput,
    TextInput
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const customerOrderService = inject(PROVIDER_CONSTANTS.customerOrderService) as CustomerOrderService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      customerOrderService,
      stateService,
      toast
    }
  },
  data() {
    let order: CustomerOrderDetails|undefined

    return {
      loading: true,
      order,
      showMenu: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
    }
  },
  computed: {
    stateDisplayValue(): string|undefined {
      if (this.order && this.order.customer.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.order?.customer.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    }
  },
  mounted() {
    this.getStates().then(() => {
      this.getOrder()
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getOrder(): void {
      this.customerOrderService.getCustomerOrder(this.id).then(order => {
        this.order = order
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your order. Try reloading the page.')
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onEditAccountInformationClicked(): void {
      this.$router.push('/my-account')
    },
    orderPartStatusToDisplayValue(status: number): string {
      switch(status) {
        case 1: return 'Needs Allocated'
        case 2: return 'Ordered'
        case 3: return 'In Stock'
        default: return ''
      }
    },
    statusText(status: number): string {
      switch(status) {
        case 1: return 'Pending'
        case 2: return 'In Progress'
        case 3: return 'Completed'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.order)
        document.title = `Order #${this.order.id} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.order-details {
  background-color: $Color-fill-gray;
  position: relative;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  > .loading-spinner {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(50%, 50%);
  }

  > .order-details-content {
    display: grid;
    column-gap: 20px;
    grid-template-areas: 
      "header"
      "left"
      "right";
      grid-template-columns: 100%;

    @include desktop {
      grid-template-columns: 340px minmax(0, 1fr);
      grid-template-areas:
        "header header"
        "left right";
    }

    > .order-details-header {
      grid-area: header;
      padding: 15px 15px 10px 15px;
      margin-bottom: 15px;
      border-bottom: 1px solid $Color-border-gray;
      display: flex;
      position: sticky;
      background-color: $Color-fill-gray;
      z-index: 100;
      top: 94px;

      @include desktop {
        flex-direction: row;
        top: 130px;
      }

      > .title-text {
        line-height: 37px;
        font-size: 24px;
        font-weight: bold;
      }
    }

    > .left-column {
      grid-area: left;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-right: 0;
      }
    }

    > .right-column {
      grid-area: right;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-left: 0;
      }
    }

    .card {
      background-color: $Color-white;
      margin-bottom: 15px;
      @include element-shadow;

      > .header {
        background-color: $Color-fill-black;
        color: $Color-white;
        padding: 15px;
        position: relative;
      }

      > .content {
        padding: 15px;

        &.customer-supplied-parts {
          white-space: pre;
        }

        > .input {
          margin-bottom: 10px;
        }

        > .input-label {
          @include input-text-field-label;
        }

        > .total {
          margin-bottom: 15px;
          font-weight: 600;
          color: $Color-font-high;
          font-size: 1.3rem;
        }

        > .scheduled-date {
          margin: 15px 0.6rem 1.538rem 15px;
          font-size: 1rem;
          line-height: 18px;
          font-weight: normal;
          color: $Color-font-high;
        }

        > .payment {
          padding: 10px 0;
          border-bottom: 1px solid $Color-border-gray;

          > .price {
            font-size: 16px;
            font-weight: 600;
            color: $Color-font-high;
          }

          > .received-by {
            font-size: 12px;
            color: $Color-font-medium;
          }
        }

        > .customer-tile {
          position: relative;
          background-color: $Color-fill-gray;
          padding: 15px;
          border-radius: 6px;
          display: flex;

          > .customer-tile-content {
            flex: 0 0 90%;
            display: flex;
            flex-direction: column;
            line-height: 24px;

            > .name {
              color: $Color-font-high;
              font-weight: 600;
            }

            > .address {
              color: $Color-font-medium;
              font-size: 15px;
            }

            > a {
              color: $Color-font-medium;
              text-decoration: none;
              font-style: italic;
              font-size: 15px;
            }
          }

          > svg {
            flex: 0 0 10%;
            margin: auto 10px;
          }

          > .menu {
            @include menu;
          }
        }

        > .overflow-wrapper {
          overflow-y: auto;
          @include sac-table;

          td.description {
            white-space: break-spaces;
          }

          td.services {
            padding-top: 15px;
            min-width: 450px;

            > .service {
              margin-bottom: 15px;

              &:last-of-type {
                margin-bottom: 0;
              }
            }
          }

          td.notes {
            min-width: 300px;
          }
        }
      }
    }
  }
}
</style>
